import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { LayerAlias, MapModes, UNITS_LIMIT, UnitTypes } from "constant";
import features from "features";
import { convertNumberToPercentages } from "helpers/convertNumberToPercentages";
import { convertPercentagesToNumber } from "helpers/convertPercentagesToNumber";
import { calculateArea } from "helpers/calculateArea";

const usePolygonHandler = (actualAreaData) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [activePolygonsListItem, setActivePolygonsListItem] = useState(null);

  useEffect(() => {
    if (actualAreaData) {
      setActivePolygonsListItem(actualAreaData);
    }
  }, [actualAreaData]);

  const openPolygonCreationModal = useCallback(
    (areaData) => {
      dispatch(
        features.modal.actions.showModal({
          modalType: "EDIT_POLYGON",
          modalProps: {
            areaData
          }
        })
      );
    },
    [dispatch]
  );

  const openPolygonDrawMode = useCallback(
    (areaData) => {
      dispatch(features.map.actions.setMapMode(MapModes.POLYGON_CREATION));

      dispatch(features.unitsManager.actions.clearCreationPolygonHistory());

      const receivedAreaData = JSON.parse(JSON.stringify(areaData));
      receivedAreaData.geo_data.coordinates.pop(); // here we remove the closing coordinate to be able to add new points to the existing polygon

      const polygonWithoutClosedPoint = {
        ...receivedAreaData,
        geo_data: {
          ...receivedAreaData.geo_data,
          coordinates: [...receivedAreaData.geo_data.coordinates]
        },
        additional: {
          ...receivedAreaData.additional,
          stroke_transparency: convertNumberToPercentages(
            receivedAreaData.additional.stroke_transparency
          ),
          fill_transparency: convertNumberToPercentages(
            receivedAreaData.additional.fill_transparency
          )
        }
      };
      dispatch(
        features.unitsManager.actions.updateCreationPolygonParameters({
          ...polygonWithoutClosedPoint
        })
      );
      setActivePolygonsListItem(polygonWithoutClosedPoint);
    },
    [dispatch]
  );

  const onPolygonDelete = useCallback(() => {
    dispatch(features.unitsManager.actions.clearCreationPolygonParameters());
    dispatch(
      features.modal.actions.showModal({
        modalType: "DELETE",
        modalProps: {
          title: "Delete area?",
          subtitle: `Are you sure you want to delete area "${actualAreaData.name}"? This action cannot be undone.`,
          onAcceptClick: () => {
            dispatch(
              features.unitsManager.actions.deleteUnitRequest({
                params: {
                  projectId: id,
                  unitId: actualAreaData.id,
                  type: UnitTypes.POLYGON,
                  alias: LayerAlias.MANUAL
                },
                onSuccess: () => {
                  dispatch(
                    features.unitsManager.actions.fetchUnitsRequest({
                      params: {
                        projectId: id,
                        limit: UNITS_LIMIT,
                        skip: 0,
                        type: UnitTypes.POLYGON,
                        alias: LayerAlias.MANUAL
                      }
                    })
                  );
                  dispatch(
                    features.unitsManager.actions.clearCreationPolygonParameters()
                  );
                  dispatch(
                    features.unitsManager.actions.clearCreationPolygonHistory()
                  );
                  dispatch(features.unitsManager.actions.unfreezeHistory());
                  dispatch(features.modal.actions.hideModal());
                }
              })
            );
          }
        }
      })
    );
  }, [dispatch, id, actualAreaData.id, actualAreaData.name]);

  const createPolygon = useCallback(
    (values) => {
      const isEditMode = !!actualAreaData.id;

      const fields = {
        ...values,
        geo_data: {
          ...values.geo_data,
          coordinates: calculateArea(
            values.geo_data?.coordinates
          ).polygonCoordinates.map(([lat, lng]) => [lng, lat])
        },
        additional: {
          ...values.additional,
          area: calculateArea(values.geo_data?.coordinates).areaInSquareMeters,
          stroke_transparency: convertPercentagesToNumber(
            values.additional.stroke_transparency
          ),
          fill_transparency: convertPercentagesToNumber(
            values.additional.fill_transparency
          )
        }
      };
      if (isEditMode) {
        dispatch(
          features.unitsManager.actions.updateUnitRequest({
            params: {
              projectId: id,
              unitId: actualAreaData.id,
              alias: LayerAlias.MANUAL,
              type: UnitTypes.POLYGON
            },
            fields
          })
        );
      } else {
        dispatch(
          features.unitsManager.actions.createUnitRequest({
            params: {
              projectId: id,
              alias: LayerAlias.MANUAL,
              type: UnitTypes.POLYGON
            },
            fields,
            onSuccess: () => {
              dispatch(
                features.unitsManager.actions.clearCreationPolygonParameters()
              );
            }
          })
        );
      }
      dispatch(features.unitsManager.actions.clearCreationPolygonParameters());
      dispatch(features.unitsManager.actions.clearCreationPolygonHistory());
      dispatch(features.unitsManager.actions.clearCreationPolygonParameters());
      dispatch(features.unitsManager.actions.clearCreationPolygonHistory());
      dispatch(features.unitsManager.actions.unfreezeHistory());
      dispatch(features.modal.actions.hideModal());
      setActivePolygonsListItem(null);
    },
    [actualAreaData.id, dispatch, id]
  );

  return {
    activePolygonsListItem,
    setActivePolygonsListItem,
    openPolygonDrawMode,
    openPolygonCreationModal,
    onPolygonDelete,
    createPolygon
  };
};

export default usePolygonHandler;
